import styled from "styled-components";
import Image from "next/image";

import {
  COLORS,
  FONTS,
  FONT_WEIGHT,
  FONT_SIZE,
  SUPER_BREAKPOINT,
  RED_GRADIENT,
  RED_GRADIENT_FIXED,
} from "../../constants/Styles";
import Link from "next/link";
import { HAMBURGER_PX } from "../Header/HeaderStyles";
import GlobalStyle from "../GlobalStyle";
import React from "react";

const BREAK_POINT = "1000px";
const CARD_HEIGHT_NUMBER = 312;
const CARD_BOTTOM_MARGIN_NUMBER = 32;
const STICKY_SIDEBAR_TOP_MARGIN = 32;

const mobileCardStyles = `
    max-width: 327px;
    margin: 1rem auto;
    padding: 1rem;
`;

const expandIconProps = {
  src: "/icons/expand_right.svg",
  alt: "expand",
  iconDimension: 11,
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${SUPER_BREAKPOINT};
  position: relative;
  margin-top: 1rem;
  padding: 0 1rem;
  margin-bottom: 2rem;

  @media (max-width: ${HAMBURGER_PX}) {
    margin-top: 0rem;
    > * {
      max-width: 325px;
      margin: 0 auto;
    }
  }
`;

const MobileHeaderText = styled.div`
  font-weight: ${FONT_WEIGHT.weight500};
  margin-bottom: 1rem;
`;

const MobileStepContainer = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
`;

const MobileCardContainer = styled.div`
  font-weight: ${FONT_WEIGHT.weight500};
  margin-bottom: 1rem;
`;

const CardHeaderText = styled.div`
  display: flex;
  align-items: center;
`;

const MobileCard = styled.div`
  flex-shrink: 0;
  border-radius: 1rem;
  background: ${COLORS.background} url("/images/vector.png");
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;
  margin: 1rem 0;
  padding: 2rem;
  display: flex;
  flex-direction: column;

  > img {
    margin: 0 0 0.5rem;
    width: 48px;
    height: auto;
    object-fit: cover;
  }
`;

const MobileCardContent = styled.div`
  font-size: ${FONT_SIZE.size24};
  font-weight: ${FONT_WEIGHT.weight500};
  font-family: ${FONTS.spaceGrotesk};
  margin-bottom: 1rem;
`;

const Vector = styled.div<{ inverse?: boolean }>`
  position: absolute;
  max-height: 100vh;
  overflow: hidden;
  z-index: -1;
  left: ${({ inverse }) => (inverse ? "350px" : "200px")};
  top: ${({ inverse }) => (inverse ? "0" : "-200px")};
`;

const CardsContainer = styled.div`
  display: flex;
  margin-top: 5rem;
  position: relative;
`;

const MobileCardsContainer = styled.div`
  margin-top: 2rem;
  position: relative;
`;

const SidebarContainer = styled.div`
  min-width: 240px;
`;

const Sidebar = styled.div<{ sticky: boolean; didScrollPastCardArea: boolean }>`
  display: flex;
  flex-direction: column;

  ${({ didScrollPastCardArea }) =>
    didScrollPastCardArea &&
    `
    position: absolute;
    bottom: 7rem;
  `}

  ${({ sticky }) =>
    sticky &&
    `
    position: fixed;
    top: ${STICKY_SIDEBAR_TOP_MARGIN}px;
  `}
`;

const Title = styled.h1`
  max-width: 900px;
  flex: 1;
  font-family: ${FONTS.spaceGrotesk};
  font-weight: ${FONT_WEIGHT.weight700};
  font-size: ${FONT_SIZE.size96};
  text-wrap: balance;

  @media (max-width: ${HAMBURGER_PX}) {
    font-size: ${FONT_SIZE.size64};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
`;

const StepContainer = styled.div`
  border-radius: 1rem;
  background-color: ${COLORS.background};
  background-image: url("/images/vector.png");
  height: ${CARD_HEIGHT_NUMBER}px;
  max-width: 756px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  margin-bottom: ${CARD_BOTTOM_MARGIN_NUMBER}px;
  padding-left: 2.5rem;
  float: right;

  > img {
    margin-right: 2.5rem;
  }
`;

const TextArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 450px;
`;

const CardImageContainer = styled.div`
  width: 200px;
  height: 200px;
  margin-right: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardContent = styled.div`
  font-size: 1.5rem;
  font-family: ${FONTS.spaceGrotesk};
  width: 100%;
  padding-right: 1.5rem;

  @media (max-width: ${BREAK_POINT}) {
    padding-right: 0;
  }
`;

const CardButtonContainer = styled.div`
  margin-top: 1.5rem;
`;

const HelpCardsContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  margin-top: 4rem;
  @media (max-width: ${HAMBURGER_PX}) {
    margin-top: 1rem;
  }
`;

const GetStartedCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6rem 5rem;
  position: relative;
  max-width: 670px;
  background: ${COLORS.tableBackground}
    url("/images/getting_started_eclipse.png") no-repeat left bottom;
  margin: 1rem;
  border-radius: 1rem;

  @media (max-width: ${HAMBURGER_PX}) {
    ${mobileCardStyles};
    background: ${COLORS.background} url("/images/getting_started_eclipse.png")
      no-repeat left bottom;
  }
`;

const TitleText = styled.div`
  font-size: ${FONT_SIZE.size32};
  font-family: ${FONTS.spaceGrotesk};
  margin-bottom: 1rem;
  text-align: center;
`;

const ContentText = styled.div`
  text-align: center;
  line-height: 1.5rem;
  margin-bottom: 1rem;
  color: ${COLORS.secondaryLight};
`;

const NeedHelpCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 0;
  max-width: 415px;
  background: ${COLORS.tableBackground};
  margin: 1rem;
  border-radius: 1rem;

  @media (max-width: ${HAMBURGER_PX}) {
    ${mobileCardStyles};
    background: ${COLORS.background};
  }
`;

const DocsButtonContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
`;

const MobileDocsButtonContainer = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    display: flex;
    flex-direction: row;
    margin-top: 1rem;
    gap: 1rem;
    justify-content: center;

    > button {
      z-index: 2;
    }
  }
`;

const IconContainer = styled.div`
  position: relative;
  margin-top: 2.5rem;
  margin-bottom: 6rem;

  img:nth-child(2) {
    position: absolute;
    top: -5rem;
    left: -5rem;
  }
`;

const ContactTextArea = styled.div`
  align-items: center;
  text-align: center;
  padding: 0 2rem;

  > p {
    margin-bottom: 1rem;

    > span {
      color: ${COLORS.secondaryLight};
    }
  }
`;

const SubheaderText = styled.div`
  font-size: ${FONT_SIZE.size18};
  width: 240px;
  margin-bottom: 1rem;
`;

const descriptionStyles = `
  color: ${COLORS.secondaryLight};
  font-weight: ${FONT_WEIGHT.weight500};
`;

const DescriptionText = styled.h2`
  ${descriptionStyles};
  width: 525px;
  font-size: ${FONT_SIZE.size18};
  margin-bottom: 1rem;
  line-height: 1.5rem;
`;

const MobileDescriptionText = styled.h2`
  ${descriptionStyles};
  font-size: ${FONT_SIZE.size16};
  margin: 1rem 0;
  text-align: center;
  line-height: 24px;
`;

const FloatingImage = styled(Image)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: -1;
`;

const InvisibleLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  &:hover {
    color: inherit;
  }
`;

const HomePageGlobalStyle = function ComponentsGlobalStyle() {
  return (
    <GlobalStyle
      background={RED_GRADIENT}
      hamburgerBackground={RED_GRADIENT_FIXED}
    />
  );
};

export {
  HomePageGlobalStyle,
  Container,
  Title,
  ButtonContainer,
  CardsContainer,
  MobileCardsContainer,
  StepContainer,
  TextArea,
  HelpCardsContainer,
  GetStartedCard,
  NeedHelpCard,
  TitleText,
  ContentText,
  DocsButtonContainer,
  IconContainer,
  ContactTextArea,
  SubheaderText,
  Sidebar,
  CARD_HEIGHT_NUMBER,
  CARD_BOTTOM_MARGIN_NUMBER,
  SidebarContainer,
  CardContent,
  CardImageContainer,
  CardButtonContainer,
  STICKY_SIDEBAR_TOP_MARGIN,
  Vector,
  MobileHeaderText,
  MobileStepContainer,
  MobileCard,
  MobileCardContainer,
  CardHeaderText,
  MobileCardContent,
  MobileDocsButtonContainer,
  MobileDescriptionText,
  DescriptionText,
  FloatingImage,
  expandIconProps,
  InvisibleLink,
};
